import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { COLORS } from "../../constants";
import {
  desenvolvedorService,
  type FoodflyProdutoTipo,
  type ProdutoMapeamentoItem,
  type ProdutoPendenteItem,
  type ProdutoSimulacaoItem,
} from "../../services/desenvolvedorService";

const qkMap = ["desenvolvedor", "mapeamento-produtos"] as const;
const qkPend = ["desenvolvedor", "mapeamento-produtos-pendentes"] as const;
const qkCode = ["desenvolvedor", "mapeamento-produtos-codigo"] as const;

type SubTab = "pendentes" | "banco" | "simular";

const TIPOS: { value: FoodflyProdutoTipo; label: string }[] = [
  { value: "bebida", label: "Bebida (fila bebidas)" },
  { value: "porcao", label: "Porção (fila porções)" },
  { value: "pizza", label: "Pizza (fila salão)" },
  { value: "paozinho", label: "Pãozinho (bordeiro)" },
  { value: "ignorar", label: "Ignorar (não entra em fila)" },
];

function labelTipo(tipo: FoodflyProdutoTipo | null): string {
  if (!tipo) return "—";
  return TIPOS.find((t) => t.value === tipo)?.label ?? tipo;
}

function labelFonte(fonte: ProdutoSimulacaoItem["fonte"]): string {
  switch (fonte) {
    case "banco":
      return "Banco";
    case "codigo":
      return "Código";
    case "sem_match":
      return "Sem match";
  }
}

function labelOrigem(origem: ProdutoPendenteItem["origem"]): string {
  switch (origem) {
    case "foodfly":
      return "FoodFly";
    case "cardapio_web":
      return "Cardápio Web";
    case "ambos":
      return "Ambos";
  }
}

const MapeamentoProdutosDev: React.FC = () => {
  const queryClient = useQueryClient();
  const [subTab, setSubTab] = useState<SubTab>("banco");
  const [dias, setDias] = useState(7);
  const [nomeOrigem, setNomeOrigem] = useState("");
  const [tipo, setTipo] = useState<FoodflyProdutoTipo>("bebida");
  const [obs, setObs] = useState("");
  const [simularTexto, setSimularTexto] = useState("");
  const [simularResultado, setSimularResultado] = useState<
    ProdutoSimulacaoItem[] | null
  >(null);

  const mapeamentosQuery = useQuery({
    queryKey: qkMap,
    queryFn: () => desenvolvedorService.listarMapeamentosProdutos(false),
  });

  const pendentesQuery = useQuery({
    queryKey: [...qkPend, dias],
    queryFn: () => desenvolvedorService.listarProdutosPendentes(dias),
    enabled: subTab === "pendentes",
  });

  const catalogoQuery = useQuery({
    queryKey: qkCode,
    queryFn: () => desenvolvedorService.listarCatalogoCodigoProdutos(),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: qkMap });
    void queryClient.invalidateQueries({ queryKey: qkPend });
  };

  const criarMutation = useMutation({
    mutationFn: () =>
      desenvolvedorService.criarMapeamentoProduto({
        nomeOrigem: nomeOrigem.trim(),
        tipo,
        observacao: obs.trim() || undefined,
      }),
    onSuccess: (item) => {
      toast.success(
        `Mapeamento salvo — "${item.nomeOrigem}" como ${labelTipo(item.tipo)}. Já vale nos próximos pedidos.`,
      );
      setSimularTexto(item.nomeOrigem);
      setNomeOrigem("");
      setTipo("bebida");
      setObs("");
      invalidate();
      setSubTab("simular");
      void desenvolvedorService
        .simularMapeamentoProdutos([item.nomeOrigem])
        .then((itens) => setSimularResultado(itens))
        .catch(() => undefined);
    },
    onError: (e: Error) => toast.error(e.message || "Falha ao criar."),
  });

  const toggleMutation = useMutation({
    mutationFn: (params: { id: number; ativo: boolean }) =>
      desenvolvedorService.atualizarMapeamentoProduto(params.id, {
        ativo: params.ativo,
      }),
    onSuccess: (item) => {
      toast.success(
        item.ativo
          ? "Mapeamento reativado."
          : "Mapeamento desativado (volta o fluxo do código).",
      );
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message || "Falha ao atualizar."),
  });

  const simularMutation = useMutation({
    mutationFn: (nomes: string[]) =>
      desenvolvedorService.simularMapeamentoProdutos(nomes),
    onSuccess: (itens) => setSimularResultado(itens),
    onError: (e: Error) => toast.error(e.message || "Falha ao simular."),
  });

  const pendentes = useMemo(
    () => pendentesQuery.data ?? [],
    [pendentesQuery.data],
  );
  const mapeamentos = useMemo(
    () => mapeamentosQuery.data ?? [],
    [mapeamentosQuery.data],
  );
  const bebidasCodigo = useMemo(
    () => catalogoQuery.data?.bebidas ?? [],
    [catalogoQuery.data],
  );

  const mapearPendente = (p: ProdutoPendenteItem) => {
    setSubTab("banco");
    setNomeOrigem(p.nomeOrigem);
    setTipo("bebida");
  };

  const tabs: { key: SubTab; label: string }[] = [
    { key: "banco", label: "Cadastrar / mapeados" },
    { key: "pendentes", label: "Pendentes" },
    { key: "simular", label: "Simular" },
  ];

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-950">
        <p className="font-semibold">Cadastre antes dos pedidos</p>
        <p className="mt-1">
          Não precisa esperar pedido chegar. Informe o nome exato do produto na
          integração (FoodFly ou Cardápio Web), escolha o tipo e salve — a
          regra já vale nos próximos webhooks. Sem regras no banco, a produção
          continua exatamente como hoje (whitelist/heurísticas do código).
          Pendentes só ajudam a achar o que já passou sem mapeamento.
        </p>
      </section>

      <section className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
        <h2 className="mb-1 text-lg font-bold text-neutral-900">
          Cadastrar mapeamento
        </h2>
        <p className="mb-3 text-sm text-neutral-600">
          Use o nome exatamente como aparece no cardápio/integração. Funciona
          mesmo sem nenhum pedido ainda.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block font-medium text-neutral-700">
              Nome exato na integração
            </span>
            <input
              value={nomeOrigem}
              onChange={(e) => setNomeOrigem(e.target.value)}
              placeholder="Ex.: Coca Zero 2L"
              className="w-full rounded-md border border-neutral-300 px-3 py-2"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium text-neutral-700">
              Tipo / fila
            </span>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value as FoodflyProdutoTipo)}
              className="w-full rounded-md border border-neutral-300 px-3 py-2"
            >
              {TIPOS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium text-neutral-700">
              Observação (opcional)
            </span>
            <input
              value={obs}
              onChange={(e) => setObs(e.target.value)}
              className="w-full rounded-md border border-neutral-300 px-3 py-2"
            />
          </label>
        </div>
        <button
          type="button"
          disabled={!nomeOrigem.trim() || criarMutation.isPending}
          className="mt-4 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          style={{ backgroundColor: COLORS.PRIMARY }}
          onClick={() => criarMutation.mutate()}
        >
          {criarMutation.isPending ? "Salvando…" : "Salvar mapeamento"}
        </button>
      </section>

      <section className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-bold text-neutral-900">
              Bebidas no código ({bebidasCodigo.length})
            </h2>
            <p className="mt-1 text-sm text-neutral-600">
              Whitelist embutida no deploy — somente leitura. Novas bebidas:
              use o formulário acima.
            </p>
          </div>
          <button
            type="button"
            className="rounded-lg border border-neutral-300 px-3 py-2 text-sm font-medium"
            onClick={() => void catalogoQuery.refetch()}
          >
            Atualizar
          </button>
        </div>
        {catalogoQuery.isLoading ? (
          <p className="text-neutral-600">Carregando…</p>
        ) : catalogoQuery.isError ? (
          <p className="text-red-600">Não foi possível carregar a whitelist.</p>
        ) : (
          <div className="max-h-48 overflow-y-auto rounded-md border border-neutral-200">
            <ul className="divide-y divide-neutral-100 text-sm">
              {bebidasCodigo.map((b) => (
                <li key={b.chave} className="px-3 py-1.5 text-neutral-800">
                  {b.nomeOrigem}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <div className="flex flex-wrap gap-2 border-b border-neutral-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setSubTab(t.key)}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
              subTab === t.key
                ? "text-white"
                : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
            }`}
            style={
              subTab === t.key ? { backgroundColor: COLORS.PRIMARY } : undefined
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "pendentes" ? (
        <section className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className="mb-1 block font-medium text-neutral-700">
                Janela (dias)
              </span>
              <input
                type="number"
                min={1}
                max={90}
                value={dias}
                onChange={(e) => setDias(Number(e.target.value) || 7)}
                className="w-24 rounded-md border border-neutral-300 px-2 py-1.5"
              />
            </label>
            <button
              type="button"
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm font-medium"
              onClick={() => void pendentesQuery.refetch()}
            >
              Atualizar
            </button>
          </div>
          <p className="mb-3 text-sm text-neutral-600">
            Itens que já apareceram em pedidos sem classificação. Se o produto
            ainda não foi pedido, cadastre no formulário acima.
          </p>
          {pendentesQuery.isLoading ? (
            <p className="text-neutral-600">Carregando…</p>
          ) : pendentes.length === 0 ? (
            <p className="text-neutral-600">
              Nenhum pendente na janela — se for produto novo, cadastre pelo
              formulário sem esperar pedido.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-neutral-200">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-neutral-50 text-xs font-semibold uppercase text-neutral-600">
                  <tr>
                    <th className="px-3 py-2">Produto</th>
                    <th className="px-3 py-2">Origem</th>
                    <th className="px-3 py-2">Chave</th>
                    <th className="px-3 py-2">Ocorrências</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {pendentes.map((p) => (
                    <tr key={p.chave}>
                      <td className="px-3 py-2 font-medium">{p.nomeOrigem}</td>
                      <td className="px-3 py-2 text-neutral-700">
                        {labelOrigem(p.origem)}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-neutral-600">
                        {p.chave}
                      </td>
                      <td className="px-3 py-2">{p.ocorrencias}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          className="rounded-md px-2 py-1 text-xs font-semibold text-white"
                          style={{ backgroundColor: COLORS.PRIMARY }}
                          onClick={() => mapearPendente(p)}
                        >
                          Mapear
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      {subTab === "banco" ? (
        <section className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 text-lg font-bold text-neutral-900">
            Regras no banco ({mapeamentos.length})
          </h2>
          {mapeamentosQuery.isLoading ? (
            <p className="text-neutral-600">Carregando…</p>
          ) : mapeamentos.length === 0 ? (
            <p className="text-neutral-600">
              Nenhum mapeamento no banco — use o formulário acima para
              cadastrar antes dos pedidos.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-neutral-200">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-neutral-50 text-xs font-semibold uppercase text-neutral-600">
                  <tr>
                    <th className="px-3 py-2">Nome</th>
                    <th className="px-3 py-2">Tipo</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {mapeamentos.map((m: ProdutoMapeamentoItem) => (
                    <tr key={m.id}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{m.nomeOrigem}</div>
                        <div className="font-mono text-[11px] text-neutral-500">
                          {m.chave}
                        </div>
                        {m.observacao ? (
                          <div className="text-xs text-neutral-500">
                            {m.observacao}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">{labelTipo(m.tipo)}</td>
                      <td className="px-3 py-2">
                        {m.ativo ? (
                          <span className="font-medium text-emerald-700">
                            Ativo
                          </span>
                        ) : (
                          <span className="text-neutral-500">Inativo</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          className="rounded-md border border-neutral-300 px-2 py-1 text-xs font-medium"
                          disabled={toggleMutation.isPending}
                          onClick={() =>
                            toggleMutation.mutate({
                              id: m.id,
                              ativo: !m.ativo,
                            })
                          }
                        >
                          {m.ativo ? "Desativar" : "Reativar"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      {subTab === "simular" ? (
        <section className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
          <h2 className="mb-2 text-lg font-bold text-neutral-900">
            Simular classificação
          </h2>
          <p className="mb-3 text-sm text-neutral-600">
            Confira o mapeamento antes dos pedidos chegarem. Um nome por linha —
            mostra se resolve pelo banco, código ou fica sem match.
          </p>
          <textarea
            value={simularTexto}
            onChange={(e) => setSimularTexto(e.target.value)}
            rows={5}
            className="w-full rounded-md border border-neutral-300 px-3 py-2 font-mono text-sm"
            placeholder={"Coca Lata\nNova Bebida XYZ"}
          />
          <button
            type="button"
            className="mt-3 rounded-lg px-4 py-2 text-sm font-semibold text-white"
            style={{ backgroundColor: COLORS.PRIMARY }}
            disabled={simularMutation.isPending}
            onClick={() => {
              const nomes = simularTexto
                .split(/\n/)
                .map((s) => s.trim())
                .filter(Boolean);
              if (nomes.length === 0) {
                toast.error("Informe ao menos um nome.");
                return;
              }
              simularMutation.mutate(nomes);
            }}
          >
            {simularMutation.isPending ? "Simulando…" : "Simular"}
          </button>
          {simularResultado ? (
            <div className="mt-4 overflow-x-auto rounded-md border border-neutral-200">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-neutral-50 text-xs font-semibold uppercase text-neutral-600">
                  <tr>
                    <th className="px-3 py-2">Nome</th>
                    <th className="px-3 py-2">Tipo</th>
                    <th className="px-3 py-2">Fonte</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {simularResultado.map((r) => (
                    <tr key={`${r.chave}-${r.nomeOrigem}`}>
                      <td className="px-3 py-2 font-medium">{r.nomeOrigem}</td>
                      <td className="px-3 py-2">{labelTipo(r.tipo)}</td>
                      <td className="px-3 py-2">{labelFonte(r.fonte)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
};

export default MapeamentoProdutosDev;
